import type { CipherDirection } from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";
import {
  buildSquare,
  type GridAlphabet,
  gridText,
  type SquareFill,
  squarePoint,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

/**
 * The Nihilist substitution cipher.
 *
 * A 5 × 5 Polybius square gives every letter a two-digit number — its row, then its
 * column, each 1–5, so 11 to 55. A second keyword's letters are numbered from the same
 * square, and the key numbers repeat along the message. Each cipher number is the
 * plain number **plus** the key number, by ordinary addition. So the sums run from 22
 * to 110.
 *
 * Two conventions for sums of 100 and over, both in use:
 *
 * - **in full** (`wrap: false`), as Wikipedia writes them: 106, 104…;
 * - **dropping the hundreds** (`wrap: true`), as the American Cryptogram Association
 *   does: 100–110 are written 00–10. No sum can be below 22, so a small number is
 *   unambiguous and decoding adds the hundred back.
 *
 * Decoding subtracts the key number and reads the difference as row and column.
 */

export interface NihilistOptions {
  /** A keyword for the square. */
  keyword: string;
  kind: Exclude<GridAlphabet, "alnum">;
  fill: SquareFill;
  /** The additive key; its letters are numbered from the same square. */
  key: string;
  /** Write 100–110 as 00–10, as the ACA does. */
  wrap: boolean;
}

export interface NihilistStep {
  /** The plain letter (the result when decoding); "?" when a number named no letter. */
  plain: string;
  /** Its number in the square, or null if there is none. */
  plainNumber: number | null;
  key: string;
  keyNumber: number;
  /** The cipher number as an integer, hundreds included. */
  sum: number;
  /** The cipher number as written. */
  written: string;
  /** False when a cipher number could not be a real letter plus key (decoding). */
  valid: boolean;
}

export interface NihilistResult {
  square: string;
  /** The key's letters in capitals; empty when the key has none. */
  key: string;
  steps: NihilistStep[];
  output: string;
  droppedQ: number;
  /** Decoding: how many numbers did not decode to a letter. */
  invalid: number;
}

const SIZE = 5;
const HUNDRED = 100;
const NUMBER = /\d+/g;

function numberOf(square: string, letter: string): number {
  const point = squarePoint(square, SIZE, letter);
  return (point.row + 1) * 10 + (point.column + 1);
}

function writeNumber(sum: number, wrap: boolean): string {
  const shown = wrap && sum >= HUNDRED ? sum - HUNDRED : sum;
  return String(shown).padStart(2, "0");
}

/** The letter at a two-digit row-column number, or "" if it is not in the square. */
function letterOf(square: string, value: number): string {
  const row = Math.floor(value / 10);
  const column = value % 10;
  if (row < 1 || row > SIZE || column < 1 || column > SIZE) {
    return "";
  }
  return square.charAt((row - 1) * SIZE + (column - 1));
}

function encode(
  text: string,
  options: NihilistOptions,
  square: string,
  key: string
): NihilistResult {
  const { chars, droppedQ } = gridText(text, options.kind);
  const steps: NihilistStep[] = [];
  for (const [index, plain] of [...chars].entries()) {
    const keyLetter = key.charAt(index % key.length);
    const plainNumber = numberOf(square, plain);
    const keyNumber = numberOf(square, keyLetter);
    const sum = plainNumber + keyNumber;
    steps.push({
      plain,
      plainNumber,
      key: keyLetter,
      keyNumber,
      sum,
      written: writeNumber(sum, options.wrap),
      valid: true,
    });
  }
  return {
    square,
    key,
    steps,
    output: steps.map((step) => step.written).join(" "),
    droppedQ,
    invalid: 0,
  };
}

function decode(
  text: string,
  options: NihilistOptions,
  square: string,
  key: string
): NihilistResult {
  const steps: NihilistStep[] = [];
  for (const [index, token] of (text.match(NUMBER) ?? []).entries()) {
    const written = Number(token);
    // Sums lie between 22 and 110, so a written value under 22 had its hundred dropped.
    const sum = options.wrap && written < 22 ? written + HUNDRED : written;
    const keyLetter = key.charAt(index % key.length);
    const keyNumber = numberOf(square, keyLetter);
    const plainNumber = sum - keyNumber;
    const plain = letterOf(square, plainNumber);
    steps.push({
      plain: plain === "" ? "?" : plain,
      plainNumber: plain === "" ? null : plainNumber,
      key: keyLetter,
      keyNumber,
      sum,
      written: token,
      valid: plain !== "",
    });
  }
  return {
    square,
    key,
    steps,
    output: steps.map((step) => step.plain).join(""),
    droppedQ: 0,
    invalid: steps.filter((step) => !step.valid).length,
  };
}

export function nihilistTransform(
  text: string,
  options: NihilistOptions,
  direction: CipherDirection
): NihilistResult {
  const square = buildSquare(options.keyword, options.kind, options.fill);
  const key = gridText(options.key, options.kind).chars;
  if (key === "") {
    return { square, key, steps: [], output: "", droppedQ: 0, invalid: 0 };
  }
  return direction === "encode"
    ? encode(text, options, square, key)
    : decode(text, options, square, key);
}
